import React, { useEffect, useState } from 'react';
import '../../styles/Tables.css';
import { useTranslation } from 'react-i18next';

function RankingsTable({ rankings, currentLanguage }) {
  const { t } = useTranslation();
  const [forceRefresh, setForceRefresh] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterText, setFilterText] = useState('');
  
  // Force re-render when language changes (kept for future translation support)
  useEffect(() => {
    setForceRefresh(prev => prev + 1);
  }, [currentLanguage]);
  
  // Format numbers according to locale (always English for now)
  const formatNumber = (num) => {
    try {
      return new Intl.NumberFormat('en-US').format(num);
    } catch (err) {
      return num.toString();
    }
  };
  
  // Sorting function
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  // Handle sort
  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };
  
  // Filter data
  const filteredData = rankings.filter(ranking => 
    (ranking.strategy || ranking.indicator).toLowerCase().includes(filterText.toLowerCase())
  );
  
  // Sort filtered data
  const sortedData = sortConfig.key ? sortData(filteredData, sortConfig.key, sortConfig.direction) : filteredData;
  
  // Calculate stats
  const totalRankings = sortedData.length;
  
  return (
    <div className="fancy-portfolio-container" key={`rankings-container-${currentLanguage}-${forceRefresh}`}>
      {/* Rankings Header */}
      <div className="portfolio-header">
        <div className="header-content">
          <div className="portfolio-stats">
            <div className="stat-badge total">
              <span className="stat-number">{formatNumber(totalRankings)}</span>
              <span className="stat-label">{t('rankings.totalRankings', 'Total Rankings')}</span>
            </div>
            <div className="search-badge">
              <div className="search-container">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder={t('rankings.searchPlaceholder', 'Search indicators...')}
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="search-input"
                />
                {filterText && (
                  <button 
                    className="clear-search"
                    onClick={() => setFilterText('')}
                    title={t('rankings.clearSearch', 'Clear search')}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Info Banner */}
      <div className="info-banner-enhanced">
        <div className="info-content">
          <span className="info-icon">📊</span>
          <div className="info-text">
            <p className="info-message">{t('rankings.infoMessage')}</p>
            <div className="info-stats">
              <span className="info-stat">
                <strong>{t('rankings.liveAgentCount')}:</strong> 6103
              </span>
              <span className="info-stat">
                <strong>{t('rankings.updateSchedule')}:</strong> {t('rankings.updateTime')}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Enhanced Table */}
      <div className="fancy-table-container">
        <table className="fancy-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('strategy')} className="sortable-header">
                <div className="header-content">
                  <span>{t('rankings.technicalIndicator')}</span>
                  {sortConfig.key === 'strategy' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th onClick={() => handleSort('rank')} className="sortable-header">
                <div className="header-content">
                  <span>{t('rankings.rank')}</span>
                  {sortConfig.key === 'rank' && (
                    <span className="sort-indicator">
                      {sortConfig.direction === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length > 0 ? (
              sortedData.map((ranking, index) => (
                <tr key={`${ranking.strategy || ranking.indicator}-${index}-${currentLanguage}`} className="holding-row">
                  <td>
                    <div className="strategy-cell">
                      <div className="strategy-badge">
                        <span className="strategy-text">{ranking.strategy || ranking.indicator}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="rank-cell">
                      <span className={`rank-badge ${ranking.rank <= 10 ? 'top' : ranking.rank <= 50 ? 'good' : 'average'}`}>
                        <span className="rank-icon">
                          {ranking.rank <= 10 ? '🥇' : ranking.rank <= 50 ? '🥈' : '🥉'}
                        </span>
                        <span className="rank-value">{formatNumber(ranking.rank)}</span>
                      </span>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr className="empty-state">
                <td colSpan="2">
                  <div className="empty-content">
                    <span className="empty-icon">📭</span>
                    <p className="empty-text">
                      {filterText 
                        ? t('rankings.noResults', 'No indicators match your search')
                        : t('rankings.noRankings', 'No rankings available')
                      }
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Rankings Footer */}
      <div className="portfolio-footer">
        <div className="footer-info">
          <span className="footer-text">
            {t('rankings.lastUpdated', 'Last Updated')}: {new Date().toLocaleString()}
          </span>
        </div>
        <div className="footer-actions">
          <button className="footer-btn" onClick={() => window.location.reload()}>
            <span className="btn-icon">🔄</span>
            <span className="btn-text">{t('rankings.refresh', 'Refresh')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default RankingsTable;